/**
 * ตรรกะล้วนของหน้าจอใบสั่งขาย (P2-4b, D-090) แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `lib/procurement/procurementView.ts` และ `lib/customers/customersView.ts`:
 * สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้โดยไม่ต้องเรนเดอร์อะไรเลย — สามข้อที่ผิดแล้ว
 * แพงที่สุดของกลุ่มนี้
 *
 * 1. **แถวนี้มีปุ่มอะไรได้บ้าง** (`salesOrderLifecycleActionsFor`) ซึ่งเป็นการอ่าน
 *    state machine ของ contract ออกมาเป็นคำตอบเดียว ไม่ใช่เงื่อนไข `if` ที่กระจาย
 *    อยู่ใน JSX — และเป็นที่เดียวที่บอกว่า **สามสถานะที่ยังไม่มีใครเขียนไม่มีปุ่ม**
 * 2. **การประกอบ body** (`toCreateSalesOrderBody`) ซึ่งเป็นด่านเดียวที่ทางเขียนของ
 *    หน้าจอนี้เดินผ่าน และเป็นที่ที่ "ใบต้องมีอย่างน้อยหนึ่งบรรทัด" ถูกบังคับก่อน
 *    ที่คำขอจะออกเดินทาง
 * 3. **จำนวนกับวันที่ที่กรอกใช้ได้หรือยัง** — ตรวจเป็นสตริงล้วน ไม่แปลงเป็น number
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่ประกอบ `status`, `so_id` หรือฟิลด์ผู้กระทำเลย** และ
 * นั่นเป็นข้อจำกัดโดยเจตนา: ทั้งหมดเป็นของเซิร์ฟเวอร์ (D-090) สถานะเปลี่ยนได้ทาง
 * เดียวคือ transition และผู้เปิดใบคือค่าที่ D-010 เอาไปเทียบกับผู้ยืนยัน
 */

import {
  CANCELLED_SO_STATUS,
  CONFIRMED_SO_STATUS,
  DELIVERED_SO_STATUS,
  DRAFT_SO_STATUS,
  PARTIALLY_DELIVERED_SO_STATUS,
  PLANNED_SO_STATUS,
  type CreateSalesOrderBody,
  type CreateSalesOrderLine,
  type SalesOrderLine,
} from './types';
import type {
  CustomerPoImportOutcome,
  SalesOrderExportFilters,
  SalesOrderExportRequest,
  SalesOrderFailure,
  SalesOrderSpreadsheetPreview,
  SalesOrderTransition,
} from './client';
import type { Product } from '../products/types';

export interface SalesOrderStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * หกสถานะของ lifecycle ตามลำดับที่ contract เขียนไว้
 *
 * ข้อความของแต่ละสถานะต้องบอกว่ามัน **ต่างกันอย่างไร** ไม่ใช่แค่แปลชื่อ — และสามตัว
 * กลางต้องบอกด้วยว่า **ใครเป็นผู้เขียนมัน** เพราะไม่ใช่หน้าจอนี้: `PLANNED` มาจาก
 * MRP (P2-6) ส่วน `PARTIALLY_DELIVERED`/`DELIVERED` มาจากงานส่งของ (P2-9) ผู้ใช้ที่
 * เห็นใบอยู่ในสามสถานะนั้นแล้วหาปุ่มไม่เจอ ต้องอ่านออกว่ามันไม่ใช่ความผิดพลาด
 */
export const SALES_ORDER_STATUS_DESCRIPTORS: readonly SalesOrderStatusDescriptor[] = [
  {
    code: DRAFT_SO_STATUS,
    label: 'ร่าง (DRAFT)',
    summary:
      'คีย์ Customer PO เข้าระบบแล้วแต่ยังไม่มีใครยืนยัน — ยังไม่ผูกมัดว่าจะผลิตหรือส่งให้ลูกค้า',
  },
  {
    code: CONFIRMED_SO_STATUS,
    label: 'ยืนยันแล้ว (CONFIRMED)',
    summary:
      'ผ่านผู้ยืนยันซึ่งต้องไม่ใช่ผู้เปิดใบ (D-010) — เป็นความต้องการจริงที่การวางแผนผลิตจะหยิบไปใช้',
  },
  {
    code: PLANNED_SO_STATUS,
    label: 'วางแผนผลิตแล้ว (PLANNED)',
    summary:
      'มีแผนผลิตครอบใบนี้แล้ว — สถานะนี้เขียนโดยงานวางแผน MRP (P2-6) ไม่ใช่หน้าจอนี้',
  },
  {
    code: PARTIALLY_DELIVERED_SO_STATUS,
    label: 'ส่งของบางส่วนแล้ว (PARTIALLY_DELIVERED)',
    summary:
      'ของออกจากโรงงานไปแล้วบางส่วน — สถานะนี้เขียนโดยงานส่งของ (P2-9) และยกเลิกไม่ได้อีก',
  },
  {
    code: DELIVERED_SO_STATUS,
    label: 'ส่งของครบแล้ว (DELIVERED)',
    summary: 'ส่งของครบตามใบแล้ว — เขียนโดยงานส่งของ (P2-9) เช่นกัน',
  },
  {
    code: CANCELLED_SO_STATUS,
    label: 'ยกเลิกแล้ว (CANCELLED)',
    summary:
      'ถอนคำสั่งซื้อก่อนของออกจากโรงงาน — ใบยังอ่านได้ตลอดไป ไม่ใช่การลบแถวทิ้ง',
  },
];

/** สีของป้ายสถานะ — บอก "ต้องทำอะไรต่อ" ไม่ใช่บอกว่าดีหรือแย่ */
export type SalesOrderStatusTone = 'neutral' | 'warning' | 'success' | 'info';

export function salesOrderStatusTone(status: string): SalesOrderStatusTone {
  switch (status) {
    case DRAFT_SO_STATUS:
      // สีเตือนเพราะมันคือใบที่ **รอคนกด** ไม่ใช่เพราะมีอะไรผิด
      return 'warning';
    case CONFIRMED_SO_STATUS:
    case DELIVERED_SO_STATUS:
      return 'success';
    case PLANNED_SO_STATUS:
    case PARTIALLY_DELIVERED_SO_STATUS:
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * ชื่อที่อ่านออกของสถานะ — **คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้นหรือคืนช่องว่าง**
 *
 * ช่องสถานะที่ว่างเปล่าอ่านเหมือนใบที่ไม่มีสถานะ ซึ่งเป็นไปไม่ได้ตาม contract
 * (คอลัมน์นี้ `NOT NULL` และมี CHECK) รหัสดิบบอกผู้ใช้ได้อย่างน้อยว่าใบอยู่ที่ไหน
 */
export function salesOrderStatusLabel(status: string): string {
  return (
    SALES_ORDER_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status
  );
}

/**
 * ใบในสถานะนี้มี transition อะไรให้กระทำได้บ้าง — **state machine ของ contract ทั้งอัน
 * อยู่ในฟังก์ชันเดียวนี้**
 *
 * `DRAFT` → ยืนยัน หรือ ยกเลิก
 * `CONFIRMED` → **ยกเลิกได้อย่างเดียว** (ขั้นถัดไปเป็นของงานวางแผนและงานส่งของ)
 * `PLANNED` / `PARTIALLY_DELIVERED` / `DELIVERED` / `CANCELLED` → **ไม่มีเลย** —
 * สามตัวแรกคือใบที่โดเมนอื่นขยับไปแล้ว (MRP และงานส่งของ) และสถานะที่หน้าจอยัง
 * ไม่รู้จักก็ไม่มีปุ่มเช่นกัน (deny-safe: ปุ่มที่เดาว่าน่าจะกดได้ คือปุ่มที่จะถูก
 * ตอบ `409`)
 *
 * ⚠️ นี่คือ **UX layer** ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์ตรวจ source status ของทุก
 * คำขอเสมอ สิ่งที่ฟังก์ชันนี้ทำคือไม่เสนอปุ่มที่จะถูกปฏิเสธแน่ ๆ ให้ผู้ใช้กด
 */
export function salesOrderLifecycleActionsFor(
  status: string,
): readonly SalesOrderTransition[] {
  switch (status) {
    case DRAFT_SO_STATUS:
      return ['confirm', 'cancel'];
    case CONFIRMED_SO_STATUS:
      return ['cancel'];
    default:
      return [];
  }
}

/**
 * สิทธิ์ที่ปุ่มแต่ละปุ่มต้องการ — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * **ทั้งสองเส้นเป็น EDIT** ต่างจากใบขอซื้อ/ใบสั่งซื้อที่การอนุมัติใช้ APPROVE:
 * contract ของกลุ่มนี้ประกาศ `confirm` ไว้ที่ EDIT ตรง ๆ และการยกระดับเป็น APPROVE
 * ที่หน้าจอจะซ่อนปุ่มที่เซิร์ฟเวอร์ยินดีจะรับอยู่แล้ว
 */
export function salesOrderActionPermission(transition: SalesOrderTransition): 'EDIT' {
  // แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็นผู้พิสูจน์ว่า transition ตัวที่
  // สามในอนาคตต้องถูกตัดสินเรื่องสิทธิ์อย่างตั้งใจ ไม่ใช่ตกลงมาที่ EDIT เงียบ ๆ
  switch (transition) {
    case 'confirm':
    case 'cancel':
      return 'EDIT';
  }
}

/** คำบนปุ่ม */
export function salesOrderActionLabel(transition: SalesOrderTransition): string {
  switch (transition) {
    case 'confirm':
      return 'ยืนยันใบ';
    case 'cancel':
      return 'ยกเลิกใบ';
  }
}

/** สถานะที่ transition นั้นพาใบไปถึง — ใช้เขียนข้อความยืนยันผลที่อ่านออก */
export function salesOrderActionTargetStatus(transition: SalesOrderTransition): string {
  switch (transition) {
    case 'confirm':
      return CONFIRMED_SO_STATUS;
    case 'cancel':
      return CANCELLED_SO_STATUS;
  }
}

/**
 * ค่าที่ตัวกรองสถานะส่งให้ `GET /v1/sales-orders?status=` — คืน `undefined` เมื่อ
 * "ทุกสถานะ"
 *
 * `status` เป็นพารามิเตอร์ที่ contract รับจริง การกรองมันฝั่งเบราว์เซอร์จะกรองแค่
 * **หน้าที่โหลดมาแล้ว** แล้วผู้ใช้ที่เลือก "ยกเลิกแล้ว" จะเห็นรายการว่างทั้งที่มีใบที่
 * ถูกยกเลิกอยู่ในหน้าถัดไป — ตั้งแต่ D-118 ข้อ 2 **คำค้นอิสระก็เดินทางไปเซิร์ฟเวอร์
 * ด้วยเหตุผลเดียวกันเป๊ะ** (ดู `SALES_ORDER_LIST_FILTER_PARAMS`)
 *
 * `undefined` ไม่ใช่สตริงว่าง — คีย์ที่ไม่ได้ระบุต้องไม่เดินทางไปเลย
 */
export function toSalesOrderStatusFilter(status: string): string | undefined {
  const trimmed = status.trim();
  return trimmed === '' ? undefined : trimmed;
}

/* --------- D-118 ข้อ 1-4 — ตัวกรองอยู่ใน URL, pagination, และคอลัมน์ใหม่ ------ */

/**
 * ชื่อ search param ของตัวกรองแต่ละตัวบนหน้ารายการ — **ชื่อเดียวกับพารามิเตอร์ของ API**
 *
 * ตัวกรองทั้งหมดอยู่ใน URL (D-118 ข้อ 2) ไม่ใช่ใน state ของ component: refresh แล้ว
 * เห็นของเดิม ส่งลิงก์ให้เพื่อนร่วมงานแล้วเขาเห็นสิ่งเดียวกัน และปุ่ม back ของ
 * เบราว์เซอร์ย้อนตัวกรองได้ — สามอย่างที่ state ในหน่วยความจำให้ไม่ได้เลย
 *
 * ใช้ชื่อเดียวกับฝั่ง API โดยตั้งใจ: ชื่อสองชุดที่แปลกันไปมาคือที่ที่ `delivery_from`
 * ของหน้าจอจะเดินทางไปเป็น `created_from` ของเซิร์ฟเวอร์ในวันที่มีคนเพิ่มตัวกรองที่เจ็ด
 */
export const SALES_ORDER_LIST_FILTER_PARAMS = {
  q: 'q',
  status: 'status',
  createdFrom: 'created_from',
  createdTo: 'created_to',
  deliveryFrom: 'delivery_from',
  deliveryTo: 'delivery_to',
} as const;

/** ชื่อ search param ของจำนวนแถวต่อหน้า — อยู่ใน URL เหมือนตัวกรอง (ลิงก์เดิมให้หน้าเดิม) */
export const SALES_ORDER_LIST_LIMIT_PARAM = 'limit';

/**
 * ค่าที่ตัวกรองบนหน้ารายการถืออยู่ — **สตริงดิบตามที่อยู่ใน URL** ไม่ใช่ `Date`
 *
 * `<input type="date">` ให้ `YYYY-MM-DD` และ API รับ `YYYY-MM-DD` ทั้งสองปลายจึงเป็น
 * สตริงรูปเดียวกัน การแปลงเป็น `Date` กลางทางจะลาก timezone ของเครื่องผู้ใช้เข้ามาใน
 * ค่าที่เซิร์ฟเวอร์อ่านตามปฏิทินไทยอยู่แล้ว (D-055) แล้ววันที่ผู้ใช้เลือกจะเลื่อนไป
 * หนึ่งวันสำหรับคนที่ตั้งเครื่องไว้คนละเขตเวลา
 */
export interface SalesOrderListFilters {
  readonly q: string;
  readonly status: string;
  readonly createdFrom: string;
  readonly createdTo: string;
  readonly deliveryFrom: string;
  readonly deliveryTo: string;
}

export const EMPTY_SALES_ORDER_LIST_FILTERS: SalesOrderListFilters = {
  q: '',
  status: '',
  createdFrom: '',
  createdTo: '',
  deliveryFrom: '',
  deliveryTo: '',
};

/** อ่านตัวกรองทั้งชุดออกจาก URL — คีย์ที่ไม่มีเป็นสตริงว่าง ("ไม่ได้กรอง") */
export function resolveSalesOrderListFilters(
  params: URLSearchParams,
): SalesOrderListFilters {
  return {
    q: params.get(SALES_ORDER_LIST_FILTER_PARAMS.q) ?? '',
    status: params.get(SALES_ORDER_LIST_FILTER_PARAMS.status) ?? '',
    createdFrom: params.get(SALES_ORDER_LIST_FILTER_PARAMS.createdFrom) ?? '',
    createdTo: params.get(SALES_ORDER_LIST_FILTER_PARAMS.createdTo) ?? '',
    deliveryFrom: params.get(SALES_ORDER_LIST_FILTER_PARAMS.deliveryFrom) ?? '',
    deliveryTo: params.get(SALES_ORDER_LIST_FILTER_PARAMS.deliveryTo) ?? '',
  };
}

/** มีตัวกรองตัวใดตัวหนึ่งทำงานอยู่หรือไม่ — แยก "ยังไม่มีใบเลย" จาก "ไม่ตรงตัวกรอง" */
export function hasAnySalesOrderListFilter(filters: SalesOrderListFilters): boolean {
  return (Object.values(filters) as readonly string[]).some(
    (value) => value.trim() !== '',
  );
}

/**
 * จำนวนแถวต่อหน้าที่ผู้ใช้เลือกได้ (D-118 ข้อ 1) — สี่ค่า และไม่มีค่าที่ห้า
 *
 * เพดาน 200 คือ `MAX_PAGE_LIMIT` ฝั่งเซิร์ฟเวอร์เป๊ะ ๆ ไม่ใช่ค่าที่หน้าจอเลือกเอง —
 * ตัวเลือกที่เกินเพดานจะเป็นตัวเลือกที่กดแล้วได้ `422` ทุกครั้ง
 */
export const SALES_ORDER_PAGE_SIZES = [25, 50, 100, 200] as const;

/** ค่าเริ่มต้นตามที่ผู้ใช้สั่ง (D-118 ข้อ 1) — ไม่ใช่ `DEFAULT_PAGE_LIMIT` (50) ของ API */
export const DEFAULT_SALES_ORDER_PAGE_SIZE = 25;

/**
 * อ่านจำนวนแถวต่อหน้าจาก URL — **ค่าที่ไม่อยู่ในสี่ตัวเลือกตกไปที่ค่าเริ่มต้น**
 *
 * ไม่ใช่การเงียบ ๆ ยอมรับค่าที่ไม่รู้จัก: `?limit=5000` ที่ผ่านไปถึงเซิร์ฟเวอร์จะได้
 * `422` ทั้งหน้า ส่วนการตกไปที่ 25 ให้หน้าจอที่ใช้งานได้พร้อมดรอปดาวน์ที่บอกว่าตอนนี้
 * อยู่ที่เท่าไร (deny-safe แบบเดียวกับ `resolveSalesOrdersView`)
 */
export function resolveSalesOrderPageSize(params: URLSearchParams): number {
  const raw = params.get(SALES_ORDER_LIST_LIMIT_PARAM);
  const parsed = raw === null ? Number.NaN : Number(raw);
  return (SALES_ORDER_PAGE_SIZES as readonly number[]).includes(parsed)
    ? parsed
    : DEFAULT_SALES_ORDER_PAGE_SIZE;
}

/**
 * URL ใหม่หลังตั้งตัวกรอง — **เขียนทับเฉพาะคีย์ของตัวกรอง คีย์อื่นคงอยู่ครบ**
 *
 * `?view=receive` ที่มีอยู่แล้ว (D-116 ข้อ 3) เป็นเหตุผลที่ฟังก์ชันนี้รับ params เดิมเข้า
 * มาแทนการสร้างชุดใหม่จากศูนย์: ตัวกรองที่ล้าง `view` ทิ้งจะเด้งผู้ใช้ออกจากหน้ารับใบ
 * กลางทาง และ `view` ที่ล้างตัวกรองทิ้งจะพากลับมาที่รายการที่ไม่ใช่รายการเดิม
 *
 * ค่าว่างถูก **ลบออกจาก URL** ไม่ใช่เขียนเป็น `q=` — URL ที่เต็มไปด้วยคีย์ว่างคือ URL
 * ที่คนส่งต่อกันไม่ได้ และ `q=` ที่เดินทางถึงเซิร์ฟเวอร์จะได้ `422` (`min_length=1`)
 */
export function salesOrderListSearch(
  params: URLSearchParams,
  filters: SalesOrderListFilters,
  pageSize: number,
): string {
  const next = new URLSearchParams(params);
  for (const key of Object.keys(SALES_ORDER_LIST_FILTER_PARAMS) as readonly (keyof SalesOrderListFilters)[]) {
    const value = filters[key].trim();
    if (value === '') {
      next.delete(SALES_ORDER_LIST_FILTER_PARAMS[key]);
    } else {
      next.set(SALES_ORDER_LIST_FILTER_PARAMS[key], value);
    }
  }
  if (pageSize === DEFAULT_SALES_ORDER_PAGE_SIZE) {
    next.delete(SALES_ORDER_LIST_LIMIT_PARAM);
  } else {
    next.set(SALES_ORDER_LIST_LIMIT_PARAM, String(pageSize));
  }
  const serialized = next.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/**
 * ช่วงวันที่กลับหัวหรือไม่ (จาก > ถึง) — **ปลายข้างเดียวไม่เคยกลับหัว**
 *
 * เซิร์ฟเวอร์รับปลายข้างเดียวได้ (`created_from` ลำพังคือ "ตั้งแต่วันนี้ขึ้นไป") จึงมี
 * แค่กรณีที่ครบสองข้างแล้วเรียงผิดที่เป็น `422` — และนั่นคือกรณีที่หน้าจอรู้คำตอบอยู่
 * แล้วจึงไม่ควรยิงคำขอไปฟัง (ดู `SALES_ORDER_CREATED_RANGE_IS_REVERSED`)
 *
 * เทียบเป็นสตริง ไม่แปลงเป็น `Date`: `YYYY-MM-DD` เรียงตามพจนานุกรมตรงกับเรียงตาม
 * เวลาเสมอ และการสร้าง `Date` จะลาก timezone ของเครื่องเข้ามาในการเปรียบเทียบที่ไม่ต้อง
 * มีมันเลย
 */
export function isReversedDateRange(from: string, to: string): boolean {
  const start = from.trim();
  const end = to.trim();
  if (start === '' || end === '') {
    return false;
  }
  return start > end;
}

/**
 * ตัวกรองชุดนี้พร้อมยิงคำขอหรือยัง — `false` เมื่อมีช่วงใดกลับหัว
 *
 * หน้าจอ **ไม่ยิงคำขอที่รู้ว่าจะโดน `422`** (D-118) และการตัดสินนั้นอยู่ที่นี่จุดเดียว
 * ไม่ใช่กระจายเป็นเงื่อนไขใน effect
 */
export function areSalesOrderListFiltersRequestable(
  filters: SalesOrderListFilters,
): boolean {
  return (
    !isReversedDateRange(filters.createdFrom, filters.createdTo) &&
    !isReversedDateRange(filters.deliveryFrom, filters.deliveryTo)
  );
}

/**
 * แปลงตัวกรองของหน้าจอเป็นพารามิเตอร์ของ API — คีย์ที่ว่างหายไปทั้งคีย์
 *
 * จุดเดียวที่ตัวกรองบนหน้าจอกลายเป็นคำขอ ทั้งของรายการและของ Export — ตัวกรองที่
 * เดินทางไปสองเส้นด้วยตรรกะสองชุดคือไฟล์ที่ไม่ตรงกับตารางที่มันถูก export ออกมา
 */
export function toSalesOrderListQueryFilters(
  filters: SalesOrderListFilters,
): SalesOrderExportFilters {
  const query: Record<string, string> = {};
  for (const [key, value] of [
    ['status', filters.status],
    ['q', filters.q],
    ['created_from', filters.createdFrom],
    ['created_to', filters.createdTo],
    ['delivery_from', filters.deliveryFrom],
    ['delivery_to', filters.deliveryTo],
  ] as const) {
    const trimmed = value.trim();
    if (trimmed !== '') {
      query[key] = trimmed;
    }
  }
  return query;
}

/**
 * body ของ Export — `so_ids` เมื่อมีการติ๊กรายใบ, `filters` เมื่อไม่มี (D-118 ข้อ 6)
 *
 * **headline ของ Work Order นี้**: ผู้ใช้ที่ติ๊กสองแถวต้องได้ไฟล์ที่มีสองแถวนั้น และ
 * ผู้ใช้ที่ไม่ติ๊กอะไรหรือกด *เลือกทั้งหมด N* ต้องได้ทุกแถวที่ตัวกรองตรง — **ไม่ใช่ id
 * นับพันที่หน้าจอต้องไปไล่เก็บจากทุกหน้าก่อน** (ซึ่งเป็น N คำขอเพื่อประกอบไฟล์เดียว
 * และจะไม่ตรงกับสิ่งที่เซิร์ฟเวอร์เห็นถ้ามีคนเพิ่มใบระหว่างนั้น)
 *
 * `selectedIds` ที่ว่างเปล่าคือ "ไม่ได้ติ๊กอะไร" ซึ่งแปลว่า `filters` — ไม่ใช่
 * `so_ids: []` ที่จะได้ไฟล์เปล่าหรือ `422`
 */
export function salesOrderExportRequest(
  filters: SalesOrderListFilters,
  selectedIds: readonly string[],
): SalesOrderExportRequest {
  return selectedIds.length === 0
    ? { filters: toSalesOrderListQueryFilters(filters) }
    : { so_ids: [...selectedIds] };
}

/** คำบนปุ่ม Export — บอกว่ากดแล้วจะได้อะไร ไม่ใช่แค่ว่ามันคือปุ่ม Export */
export function salesOrderExportLabel(
  selectedCount: number,
  total: number,
): string {
  return selectedCount === 0
    ? `Export ทั้งหมด ${total} รายการ`
    : `Export ${selectedCount} รายการที่เลือก`;
}

/**
 * ช่วงวันส่งของใบหนึ่งใบ (D-118 ข้อ 4) — วันเดียวเมื่อทุกบรรทัดตรงกัน ช่วงเมื่อต่างกัน
 *
 * ขีดคั่นเป็น en dash มีช่องว่างสองข้าง ค่าเดียวกับ `DELIVERY_RANGE_SEPARATOR` ของ
 * `service.py` — ยัติภังค์จะถูกอ่านเป็นส่วนหนึ่งของวันที่ ISO ที่อยู่ข้างมัน
 *
 * ใบที่ไม่มีวันส่งเลย (ไม่มีบรรทัด ซึ่งไม่มี route ใดสร้างได้) แสดงเป็น "—" ที่มองเห็น
 * ได้ ไม่ใช่ช่องว่างเปล่าที่อ่านไม่ออกว่าเป็น "ไม่มีค่า" หรือ "ยังโหลดไม่เสร็จ"
 */
export const SALES_ORDER_DELIVERY_RANGE_SEPARATOR = ' – ';

export function salesOrderDeliverySpanLabel(
  from: string | null,
  to: string | null,
): string {
  if (from === null && to === null) {
    return '—';
  }
  if (from === null || to === null) {
    return from ?? to ?? '—';
  }
  return from === to ? from : `${from}${SALES_ORDER_DELIVERY_RANGE_SEPARATOR}${to}`;
}

/**
 * "แถว 26–50 จาก 413" (D-118 ข้อ 1) — สตริงว่างเมื่อหน้านี้ไม่มีแถวเลย
 *
 * `firstRowNumber` มาจากจำนวนหน้าที่เดินผ่านมาแล้ว × จำนวนแถวต่อหน้า ซึ่งถูกเพราะ
 * **มีแต่หน้าสุดท้ายที่สั้นกว่าเต็มหน้าได้** ใน cursor pagination — ไม่ใช่เพราะหน้าจอ
 * นับ offset (ซึ่งมันไม่มี ดู `SalesOrderListPage.total`)
 */
export function salesOrderRowRangeLabel(
  firstRowNumber: number,
  rowsOnPage: number,
  total: number,
): string {
  if (rowsOnPage === 0) {
    return '';
  }
  return `แถว ${firstRowNumber}–${firstRowNumber + rowsOnPage - 1} จาก ${total}`;
}

/**
 * จำนวนที่กรอกใช้ได้หรือยัง — ตรวจ **เป็นสตริง ไม่แปลงเป็น number แม้แต่ครั้งเดียว**
 *
 * `NUMERIC(18, 4)` เก็บได้ถึง 14 หลักหน้าจุดและ 4 หลังจุด ซึ่งกว้างกว่าที่ IEEE-754
 * double เก็บได้ครบ การตรวจด้วย `Number(value) > 0` จะ "ผ่าน" ค่าที่ปัดไปแล้ว และ
 * ค่าที่ปัดแล้วคือจำนวนที่ลูกค้าไม่ได้สั่ง
 *
 * ศูนย์ (`0`, `0.0000`) ถูกปฏิเสธที่นี่ด้วย ตรงกับ contract ที่บังคับ `quantity > 0`
 * — บรรทัดที่สั่งศูนย์ชิ้นไม่ได้สั่งอะไรเลย และปุ่มที่กดแล้วรู้ผลทันทีดีกว่าการ
 * เดินทางไปกลับหนึ่งรอบเพื่อบอกสิ่งที่หน้าจอรู้อยู่แล้ว
 */
export function isPositiveQuantity(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d{1,14}(\.\d{1,4})?$/.test(trimmed)) {
    return false;
  }
  return /[1-9]/.test(trimmed);
}

/**
 * แปลงค่าจากช่อง `<input type="date">` (`YYYY-MM-DD`) เป็น ISO-8601 **ที่มี offset**
 *
 * ส่งสตริง `"2026-11-15"` ดิบ ๆ ไปไม่ได้: contract บังคับว่า
 * `requested_delivery_date` เป็น **instant ที่มี offset** (D-054) และ `datetime`
 * ฝั่งเซิร์ฟเวอร์จะอ่านวันที่ลอย ๆ เป็นเที่ยงคืน **โดยไม่มีเขตเวลา** แล้วเก็บเป็น
 * UTC ซึ่งสำหรับโรงงานที่ `+07:00` คือเย็นของ **วันก่อนหน้า** — วันที่ลูกค้าต้องการ
 * ของกลายเป็นคนละวันเงียบ ๆ และคนละวันในที่นี้คือของไปถึงช้าไปหนึ่งวัน
 *
 * ค่าที่คืนคือ **เที่ยงคืนตามเวลาเครื่องผู้ใช้** ในรูป instant ที่ระบุ offset ชัดเจน
 * (`toISOString()` ให้รูป `Z`) วันที่ที่ผู้ใช้เลือกจึงเดินทางกลับมาเป็นวันเดิมเสมอ
 * เมื่ออ่านด้วย `requestedDeliveryDateLabel()`
 *
 * คืน `null` เมื่อรูปแบบไม่ใช่ `YYYY-MM-DD` หรือเป็นวันที่ที่ไม่มีอยู่จริง (เช่น
 * `2026-02-30` ซึ่ง `Date` จะเลื่อนให้เป็นต้นเดือนถัดไปโดยไม่บอกใคร)
 */
export function toRequestedDeliveryDateIso(dateValue: string): string | null {
  const trimmed = dateValue.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match === null) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const local = new Date(year, month - 1, day, 0, 0, 0, 0);
  // `Date` รับ `2026-02-30` แล้วเลื่อนเป็น `2026-03-02` ให้เงียบ ๆ — เทียบกลับ
  // ทีละส่วนคือวิธีเดียวที่จะเห็นว่ามันเลื่อน
  if (
    local.getFullYear() !== year ||
    local.getMonth() !== month - 1 ||
    local.getDate() !== day
  ) {
    return null;
  }
  return local.toISOString();
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * วันที่ลูกค้าต้องการของในรูป `YYYY-MM-DD` **ตามเวลาเครื่องผู้ใช้**
 *
 * คู่กับ `toRequestedDeliveryDateIso()` — สิ่งที่ผู้ใช้เลือกในฟอร์มคือสิ่งที่เขาเห็น
 * ในตาราง
 *
 * ค่าที่อ่านไม่ออกถูกคืนตามเดิม **ไม่ใช่แสดงเป็นช่องว่างหรือ "Invalid Date"**:
 * วันที่ที่หายไปอ่านเหมือน "บรรทัดนี้ไม่ได้ระบุว่าต้องส่งเมื่อไร" ซึ่งเป็นไปไม่ได้
 * ตาม contract
 */
export function requestedDeliveryDateLabel(isoValue: string): string {
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) {
    return isoValue;
  }
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/**
 * ค่าที่หัวใบของฟอร์มสร้างใบสั่งขายถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์
 *
 * **ไม่มี `soNumber`** (D-113, D-116 ข้อ 6) — เซิร์ฟเวอร์ออกเลขเองเสมอ ช่องนี้ถูก
 * เอาออกทั้งชนิดข้อมูล ไม่ใช่แค่ไม่แสดงในฟอร์ม
 */
export interface CreateSalesOrderFields {
  readonly plantCode: string;
  readonly customerCode: string;
  readonly customerPoNumber: string;
  readonly note: string;
}

/**
 * หนึ่งบรรทัดที่ฟอร์มถืออยู่ — **ทั้งบรรทัดกรอกด้วยมือ** หรือเติมมาจากพรีวิวของไฟล์
 * ที่ upload (D-116 ข้อ 5) ก็ได้ — ทั้งสองทางแก้ต่อในตารางเดียวกันได้เหมือนกัน
 *
 * ต่างจาก `PurchaseOrderLineDraft` ที่ถือแค่เลขใบขอซื้อกับราคา: ที่นี่ไม่มีเอกสาร
 * ภายในใบไหนให้คัดลอกมา บรรทัดคือสิ่งที่อยู่บน Customer PO ของลูกค้า
 *
 * `ref1`..`ref6` เป็นสตริงดิบเหมือนช่องอื่น (สตริงว่าง = ยังไม่ได้กรอก) — การแปลง
 * เป็น `null`/ตัด key ทิ้งเป็นหน้าที่ของ `toCreateSalesOrderBody`
 *
 * `productName`/`model` (D-117 ข้อ 1) เป็น **ค่าแสดงผลอย่างเดียว ไม่ถูกส่งไปกับ
 * payload เลย** — `toCreateSalesOrderBody`/`serializeCreateSalesOrderBody` ประกอบ
 * body ทีละคีย์และไม่แตะสองฟิลด์นี้ ชื่อและโมเดลเป็นของ Product Master เก็บซ้ำบน
 * บรรทัด SO คือสำเนาที่สองที่จะไม่ตรงกันในวันที่มีคนแก้ชื่อสินค้า (D-095/D-099)
 * สตริงว่างแปลว่า "ยังไม่ได้ resolve" — แก้ `productCode` แล้วต้องกลับมาว่างทันที
 * (ดู `withClearedProductCode`/`withResolvedProduct`)
 */
export interface SalesOrderLineDraft {
  readonly customerPoLineNo: string;
  readonly productCode: string;
  readonly productName: string;
  readonly model: string;
  readonly quantity: string;
  readonly uomCode: string;
  /** ค่าจาก `<input type="date">` — `YYYY-MM-DD` */
  readonly requestedDeliveryDate: string;
  readonly ref1: string;
  readonly ref2: string;
  readonly ref3: string;
  readonly ref4: string;
  readonly ref5: string;
  readonly ref6: string;
}

/** บรรทัดเปล่าที่ปุ่ม "เพิ่มบรรทัด" วางลงไป */
export const EMPTY_SALES_ORDER_LINE_DRAFT: SalesOrderLineDraft = {
  customerPoLineNo: '',
  productCode: '',
  productName: '',
  model: '',
  quantity: '',
  uomCode: '',
  requestedDeliveryDate: '',
  ref1: '',
  ref2: '',
  ref3: '',
  ref4: '',
  ref5: '',
  ref6: '',
};

/* --------------------------------------- D-117 — Part Number เป็นช่องค้นหา -- */

/** ความยาวคำค้นต่ำสุดก่อนจะยิง `GET /v1/products` — พิมพ์น้อยกว่านี้ไม่ค้น */
export const PRODUCT_SEARCH_MIN_QUERY_LENGTH = 2;

/** คำค้นยาวพอจะยิงค้นหาหรือยัง (D-117 ข้อ 2) — ทุกการกดแป้นไม่ใช่ทุกคำขอ */
export function isProductSearchQueryEligible(query: string): boolean {
  return query.trim().length >= PRODUCT_SEARCH_MIN_QUERY_LENGTH;
}

/** ข้อความหนึ่งตัวเลือกในดรอปดาวน์ — รหัส + ชื่อ + โมเดล (D-117 ข้อ 2) */
export function productOptionLabel(
  product: Pick<Product, 'product_code' | 'name' | 'model'>,
): string {
  return product.model === null || product.model.trim() === ''
    ? `${product.product_code} — ${product.name}`
    : `${product.product_code} — ${product.name} (${product.model})`;
}

/**
 * สินค้าที่รหัส**ตรงทั้งตัว** กับ `code` — ไม่ใช่แค่ขึ้นต้นด้วย (D-117 ข้อ 6)
 *
 * ใช้ตอนช่อง Part Number หลุดโฟกัสหลังพิมพ์รหัสเต็มเอง: ผลค้นหาที่ตรงแค่บางส่วน
 * (`FG-BOLT-M8` ตรงกับคำค้น `FG-BOLT`) ไม่ควรถูกเติมให้อัตโนมัติ — ผู้ใช้ต้องเลือก
 * จากตัวเลือกเองถ้าตั้งใจจะหมายถึงสินค้าตัวอื่น
 */
export function findProductByExactCode<T extends { readonly product_code: string }>(
  products: readonly T[],
  code: string,
): T | null {
  const trimmed = code.trim();
  if (trimmed === '') {
    return null;
  }
  return products.find((product) => product.product_code === trimmed) ?? null;
}

/**
 * ตั้งบรรทัดให้ตรงกับสินค้าที่ resolve แล้ว — ทางเดียวที่ `productName`/`model`/
 * `uomCode` ของบรรทัดถูกตั้งจากทะเบียนสินค้า (D-117 ข้อ 1, 5)
 *
 * **`uomCode` ถูกเขียนทับด้วย `base_uom` เสมอที่นี่** — ใช้ได้เฉพาะตอนเลือกจาก
 * ตัวเลือกหรือพิมพ์รหัสเต็มตรงกันเท่านั้น (D-117 ข้อ 5) **ห้ามเรียกกับแถวที่มาจาก
 * upload** เพราะแถวเหล่านั้นต้องคงหน่วยตามไฟล์ที่ลูกค้าระบุมา ไม่ใช่ค่าจาก db —
 * การเขียนทับเงียบ ๆ คือการแก้คำสั่งซื้อของลูกค้า (ดู `salesOrderLineDraftFromPreview`
 * ซึ่งอ่าน `uom_code` จากพรีวิวตรง ๆ ไม่ผ่านฟังก์ชันนี้)
 */
export function withResolvedProduct(
  draft: SalesOrderLineDraft,
  product: Pick<Product, 'product_code' | 'name' | 'model' | 'base_uom'>,
): SalesOrderLineDraft {
  return {
    ...draft,
    productCode: product.product_code,
    productName: product.name,
    model: product.model ?? '',
    uomCode: product.base_uom,
  };
}

/**
 * แก้รหัสสินค้าแล้วล้างชื่อ/โมเดลเดิมทันที (D-117 ข้อ 1, headline ของ Work Order นี้)
 *
 * ชื่อที่ไม่ตรงกับรหัสบนหน้าจอคืออันตรายที่สุดในตารางนี้ — แก้รหัสแล้วยังเห็นชื่อ
 * สินค้าตัวเดิมค้างอยู่คือหน้าจอที่โกหกว่ากำลังจะสั่งสินค้าตัวที่ไม่ได้พิมพ์รหัสไว้
 * **ไม่แตะ `uomCode`** — หน่วยที่แก้ไว้เองยังคงอยู่จนกว่าจะมีการ resolve สินค้าใหม่
 * จริง ๆ (`withResolvedProduct`) เพราะการเติมหน่วยเป็นค่าเริ่มต้น ไม่ใช่การล็อก
 */
export function withClearedProductCode(
  draft: SalesOrderLineDraft,
  productCode: string,
): SalesOrderLineDraft {
  return { ...draft, productCode, productName: '', model: '' };
}

/**
 * ประกอบ body ของ `POST /v1/sales-orders` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * เงื่อนไขที่ต้องครบ: สี่ช่องของหัวใบไม่ว่าง **มีอย่างน้อยหนึ่งบรรทัด** และทุกบรรทัด
 * กรอกครบและใช้ได้ — บรรทัดเดียวที่ผิดรูปทำให้ **ทั้งใบ** ประกอบไม่ได้ ไม่ใช่ถูก
 * ตัดทิ้งเงียบ ๆ: การรับ Customer PO เป็น all-or-nothing ฝั่งเซิร์ฟเวอร์อยู่แล้ว และ
 * ใบที่ส่งไปโดยขาดบรรทัดที่ผู้ใช้ตั้งใจใส่ คือของที่ลูกค้าจะไม่ได้รับโดยไม่มีใครบอก
 *
 * **"อย่างน้อยหนึ่งบรรทัด" ถูกบังคับที่นี่** ทำให้ปุ่มบันทึกกดไม่ได้เลยเมื่อยังไม่มี
 * บรรทัด — `422 SALES_ORDER_HAS_NO_LINES` ฝั่งเซิร์ฟเวอร์ยังเป็นด่านจริง แต่เป็น
 * ด่านที่ผู้ใช้ไม่ควรต้องเจอจากหน้าจอนี้
 *
 * **ไม่ตรวจว่า `customer_po_line_no` ซ้ำกันเองหรือไม่** โดยตั้งใจ: เลขบรรทัดมาจาก
 * เอกสารของลูกค้า และเซิร์ฟเวอร์เป็นผู้ถือกฎนั้น (`422
 * CUSTOMER_PO_LINE_NO_DUPLICATED`) พร้อมคำอธิบายที่หน้าจอแปลให้อ่านออก — ด่านที่สอง
 * ฝั่ง client จะทำให้กฎนั้นไม่มีวันถูกแสดง และถ้านิยาม "ซ้ำ" ของสองฝั่งไม่ตรงกัน
 * (เช่นเรื่องช่องว่างหรือตัวพิมพ์) หน้าจอจะปฏิเสธใบที่เซิร์ฟเวอร์ยินดีจะรับ
 *
 * ทุกรหัสถูก `trim()` เพราะช่องว่างหน้า/หลังเป็นสิ่งที่มองไม่เห็นบนหน้าจอแต่ **ติดอยู่
 * กับ business key ตลอดไป**: `so_number` แก้ไม่ได้หลังสร้าง และ `customer_po_number`
 * คือค่าที่ unique constraint ใช้กันการรับซ้ำ — เลขที่มีช่องว่างติดมาจากการคัดลอก
 * จึงเป็นเลขที่ระบบมองว่า **ไม่ซ้ำ** กับเลขเดียวกันที่ไม่มีช่องว่าง แล้วใบเดียวกัน
 * จะถูกรับเข้าสองครั้ง
 *
 * `note` ที่ว่างเปล่ากลายเป็น **คีย์ที่หายไปทั้งคีย์** ไม่ใช่สตริงว่างที่ถูกส่งออกไป
 * — `min_length=1` ฝั่งเซิร์ฟเวอร์จะตอบ `400` ให้กับสตริงว่าง ทั้งที่ผู้ใช้แค่ไม่ได้
 * กรอกช่องที่ไม่บังคับ
 */
export function toCreateSalesOrderBody(
  fields: CreateSalesOrderFields,
  lines: readonly SalesOrderLineDraft[],
): CreateSalesOrderBody | null {
  const plantCode = fields.plantCode.trim();
  const customerCode = fields.customerCode.trim();
  const customerPoNumber = fields.customerPoNumber.trim();

  if (plantCode === '' || customerCode === '' || customerPoNumber === '' || lines.length === 0) {
    return null;
  }

  const serializedLines: CreateSalesOrderLine[] = [];
  for (const line of lines) {
    const customerPoLineNo = line.customerPoLineNo.trim();
    const productCode = line.productCode.trim();
    const quantity = line.quantity.trim();
    const uomCode = line.uomCode.trim();
    const requestedDeliveryDate = toRequestedDeliveryDateIso(line.requestedDeliveryDate);

    if (
      customerPoLineNo === '' ||
      productCode === '' ||
      uomCode === '' ||
      requestedDeliveryDate === null ||
      !isPositiveQuantity(quantity)
    ) {
      return null;
    }

    // D-107 ข้อ 2 — ช่องว่างเป็น `null` ("ไม่ได้บันทึก") ไม่ใช่ `""` ("บันทึกเป็น
    // ค่าว่าง") สองอย่างนี้ต้องแยกจากกันตลอดทางจนถึง backend
    const ref1 = line.ref1.trim();
    const ref2 = line.ref2.trim();
    const ref3 = line.ref3.trim();
    const ref4 = line.ref4.trim();
    const ref5 = line.ref5.trim();
    const ref6 = line.ref6.trim();

    serializedLines.push({
      customer_po_line_no: customerPoLineNo,
      product_code: productCode,
      quantity,
      uom_code: uomCode,
      requested_delivery_date: requestedDeliveryDate,
      ref1: ref1 === '' ? null : ref1,
      ref2: ref2 === '' ? null : ref2,
      ref3: ref3 === '' ? null : ref3,
      ref4: ref4 === '' ? null : ref4,
      ref5: ref5 === '' ? null : ref5,
      ref6: ref6 === '' ? null : ref6,
    });
  }

  const note = fields.note.trim();

  return {
    plant_code: plantCode,
    customer_code: customerCode,
    customer_po_number: customerPoNumber,
    ...(note === '' ? {} : { note }),
    lines: serializedLines,
  };
}

/* --------------------------------------- D-107 ข้อ 2 — ref1..ref6 ของบรรทัด -- */

/** หนึ่งตำแหน่งอ้างอิงของบรรทัด — `heading` คือคีย์ตรงตัวจาก contract เสมอ */
export interface SalesOrderLineRef {
  readonly heading: 'ref1' | 'ref2' | 'ref3' | 'ref4' | 'ref5' | 'ref6';
  readonly value: string | null;
}

/**
 * ref1..ref6 ของบรรทัดหนึ่งบรรทัด เรียงตามตำแหน่งเสมอ — จุดเดียวที่แจงหกฟิลด์นี้ออก
 * เป็นรายการ เพื่อให้ตารางที่ต้องแสดงมันวน `.map()` แทนการเขียนหกคอลัมน์ซ้ำมือ
 *
 * `heading` เป็น **คีย์ดิบ** (`"ref1"`..`"ref6"`) ไม่ใช่ป้ายที่แปลความหมาย เพราะไม่มี
 * ความหมายตายตัวติดกับตำแหน่งใดเลย (D-107 ข้อ 2) — ป้ายที่บอกความหมายจะเป็นคำสัญญา
 * ที่ผิดสำหรับลูกค้ารายถัดไปเสมอ
 */
export function salesOrderLineRefs(line: SalesOrderLine): readonly SalesOrderLineRef[] {
  return [
    { heading: 'ref1', value: line.ref1 },
    { heading: 'ref2', value: line.ref2 },
    { heading: 'ref3', value: line.ref3 },
    { heading: 'ref4', value: line.ref4 },
    { heading: 'ref5', value: line.ref5 },
    { heading: 'ref6', value: line.ref6 },
  ];
}

/* ------------------------------- D-107 ข้อ 4 — สามคำตอบของการอัปโหลด (D-092) -- */

export interface CustomerPoImportOutcomeDescriptor {
  readonly code: CustomerPoImportOutcome;
  readonly label: string;
  readonly summary: string;
  readonly tone: SalesOrderStatusTone;
}

/**
 * สามคำตอบที่ D-092 ตัดสิน — ข้อความต้องบอก **สิ่งที่เกิดขึ้นจริง** ในคำของเซิร์ฟเวอร์
 * ไม่ใช่ตัดสินแทนว่ามันดีหรือแย่ โดยเฉพาะ `DUPLICATE_IGNORED` ซึ่งเป็นคำตอบปกติที่
 * ถูกต้อง ไม่ใช่ความล้มเหลว (ดู invariant ของ Work Order นี้)
 */
export const CUSTOMER_PO_IMPORT_OUTCOME_DESCRIPTORS: readonly CustomerPoImportOutcomeDescriptor[] =
  [
    {
      code: 'CREATED',
      label: 'สร้างใบสั่งขายใหม่ (CREATED)',
      summary:
        'ยังไม่เคยเห็น Customer PO นี้มาก่อน — ระบบเปิดใบสั่งขายใหม่ที่ DRAFT ให้จากไฟล์นี้แล้ว',
      tone: 'success',
    },
    {
      code: 'DUPLICATE_IGNORED',
      label: 'พบใบเดิมที่ตรงกันทุกบรรทัด (DUPLICATE_IGNORED)',
      summary:
        'ไฟล์นี้ตรงกับ Customer PO ที่รับเข้าไปแล้วทุกบรรทัด — เป็นคำตอบปกติ ไม่ใช่ความผิดพลาด และไม่มีอะไรถูกเขียนซ้ำ',
      tone: 'info',
    },
    {
      code: 'REVISION_RAISED',
      label: 'ยกฉบับแก้ไขไว้รออนุมัติ (REVISION_RAISED)',
      summary:
        'บรรทัดในไฟล์ต่างจากใบเดิมที่มีอยู่ — เปิดคำขอแก้ไข (revision) ไว้รออนุมัติแล้ว ใบเดิมยังไม่ถูกแก้จนกว่าจะมีคนอนุมัติ',
      tone: 'warning',
    },
  ];

export function customerPoImportOutcomeDescriptor(
  outcome: string,
): CustomerPoImportOutcomeDescriptor | undefined {
  return CUSTOMER_PO_IMPORT_OUTCOME_DESCRIPTORS.find((entry) => entry.code === outcome);
}

/** ชื่อที่อ่านออกของ outcome — คืนค่าดิบเมื่อไม่รู้จัก เหตุผลเดียวกับ `salesOrderStatusLabel` */
export function customerPoImportOutcomeLabel(outcome: string): string {
  return customerPoImportOutcomeDescriptor(outcome)?.label ?? outcome;
}

/** คำอธิบายเต็มของ outcome — สตริงว่างเมื่อไม่รู้จัก (ป้ายดิบพอแล้วในกรณีนั้น) */
export function customerPoImportOutcomeSummary(outcome: string): string {
  return customerPoImportOutcomeDescriptor(outcome)?.summary ?? '';
}

export function customerPoImportOutcomeTone(outcome: string): SalesOrderStatusTone {
  return customerPoImportOutcomeDescriptor(outcome)?.tone ?? 'neutral';
}

/* ------------------------------ D-107 ข้อ 4 — แถวและคอลัมน์ของ 422 ที่ถูกปฏิเสธ -- */

export interface SpreadsheetRefusalLocation {
  readonly row: number | null;
  readonly column: string | null;
}

/**
 * แถว (1-based ตามที่ Excel นับ) และคอลัมน์ (heading) จาก 422 ของการอัปโหลด —
 * `null` ทั้งคู่เมื่อ failure ไม่ใช่ `rule-violation` เลย (401/403/409/5xx/เครือข่าย)
 * เพื่อให้หน้าจอตัดสินใจได้ว่าจะแสดงอะไรแทน
 *
 * headline ของ Work Order นี้: ถ้าไม่มีทั้งแถวและคอลัมน์ (เช่นความล้มเหลวเรื่องไฟล์
 * ทั้งไฟล์อย่าง "ไม่ใช่ workbook" หรือ "ใหญ่เกินไป" ซึ่งไม่ใช่เรื่องของเซลล์ใดเซลล์
 * หนึ่ง) หน้าจอยังต้องแสดงข้อความของเซิร์ฟเวอร์ ไม่ใช่ปฏิเสธที่จะแสดงอะไรเลย
 */
export function spreadsheetImportFailureLocation(
  failure: SalesOrderFailure,
): SpreadsheetRefusalLocation {
  if (failure.status !== 'rule-violation') {
    return { row: null, column: null };
  }
  const { row, column } = failure.error.details;
  return {
    row: typeof row === 'number' ? row : null,
    column: typeof column === 'string' && column.trim() !== '' ? column : null,
  };
}

/* --------------------------- D-116 ข้อ 3 — drill-down เป็น search param ----- */

/**
 * `?view=receive` บน `/sales/sales-orders` — **ไม่ใช่ nested route ใหม่**
 *
 * แนวเดียวกับ `resolveProductView`/`createSearch` ของ `ProductMasterScreen`
 * (`desktop/products/productView.ts`, D-107 ข้อ 6): หน้าย่อยของ Sales Order เป็น
 * search param บน route เดิมเส้นเดียว (`routes.ts` ลงทะเบียน `/sales/sales-orders`
 * ไว้เส้นเดียว ห้ามมีเส้นที่สอง) ไม่ใช่ path ย่อยใหม่ — bookmark ได้ ปุ่ม back
 * ทำงาน และ breadcrumb/เมนูซ้ายยังชี้ path เดิมถูกต้อง
 */
export const SALES_ORDERS_VIEW_PARAM = 'view';
export const RECEIVE_SALES_ORDER_VIEW_VALUE = 'receive';

export type SalesOrdersView = { readonly kind: 'list' } | { readonly kind: 'receive' };

/** แปลง query เป็นหน้าจอที่ต้องแสดง — ค่าที่ไม่รู้จักตกไปที่รายการเสมอ (deny-safe) */
export function resolveSalesOrdersView(params: URLSearchParams): SalesOrdersView {
  return params.get(SALES_ORDERS_VIEW_PARAM) === RECEIVE_SALES_ORDER_VIEW_VALUE
    ? { kind: 'receive' }
    : { kind: 'list' };
}

/**
 * search string ของหน้ารับใบสั่งซื้อของลูกค้า — ตัวเดียวที่ประกอบสตริงนี้
 *
 * `params` (ถ้าส่งมา) คือ query ที่หน้ารายการถืออยู่ตอนนี้ และมันถูก **เก็บไว้ครบ** —
 * D-118 ใส่ตัวกรองหกตัวลงใน URL เดียวกับ `view` (ดู `salesOrderListSearch`) ลิงก์ที่พา
 * ไปหน้ารับใบแล้วล้างตัวกรองทิ้ง จะพาผู้ใช้กลับมาที่รายการที่ไม่ใช่รายการเดิมที่เขาจากมา
 *
 * เมนูซ้าย (`desktop/navigation.ts`) เรียกโดยไม่ส่ง params เพราะมันเข้าหน้านี้จากที่อื่น
 * จึงไม่มีตัวกรองของหน้ารายการให้รักษาไว้แต่แรก
 */
export function receiveSalesOrderSearch(params?: URLSearchParams): string {
  const next = new URLSearchParams(params);
  next.set(SALES_ORDERS_VIEW_PARAM, RECEIVE_SALES_ORDER_VIEW_VALUE);
  return `?${next.toString()}`;
}

/**
 * มีข้อมูลค้างอยู่ในฟอร์มรับใบหรือไม่ — ตัดสินว่าต้องถามยืนยันก่อนออกจากหน้า
 * (D-116 ข้อ 2)
 *
 * ตรวจแบบ "มีอะไรพิมพ์ไว้บ้างไหม" ไม่ใช่ "ครบพอจะส่งหรือยัง" (นั่นเป็นหน้าที่ของ
 * `toCreateSalesOrderBody`) — ผู้ใช้ที่พิมพ์ไปครึ่งช่องแล้วเผลอกด back ต้องถูกถาม
 * เหมือนกับคนที่กรอกครบแล้ว
 */
export function hasSalesOrderReceiveDraftContent(
  fields: CreateSalesOrderFields,
  lines: readonly SalesOrderLineDraft[],
): boolean {
  const headerFilled = (Object.values(fields) as readonly string[]).some(
    (value) => value.trim() !== '',
  );
  if (headerFilled) {
    return true;
  }
  return lines.some((line) =>
    (Object.values(line) as readonly string[]).some((value) => value.trim() !== ''),
  );
}

/* ------------------------ D-116 ข้อ 5 — พรีวิวเติมลงตารางเดียวกับกรอกมือ ------ */

/**
 * แปลงบรรทัดที่พรีวิวอ่านออกมาให้เป็นแถวที่ฟอร์มแก้ต่อได้ — **เติมลงตารางเดียวกับที่
 * กรอกมือ** (D-116 ข้อ 5) ไม่ใช่ตารางอ่านอย่างเดียวแยกต่างหาก ผู้ใช้แก้ค่าต่อได้ก่อน
 * กดส่งจริงเหมือนบรรทัดที่พิมพ์เอง
 *
 * `requested_delivery_date` แปลงกลับเป็น `YYYY-MM-DD` ด้วย `requestedDeliveryDateLabel`
 * ตัวเดียวกับที่ตารางแสดงผลใช้ — ไม่งั้นช่อง `<input type="date">` จะอ่านค่า ISO
 * เต็มรูปแบบไม่ออกแล้วแสดงว่างเปล่า ทั้งที่พรีวิวอ่านวันที่ออกมาได้ถูกต้องแล้ว
 *
 * **`productName`/`model`/`uomCode` มาจากพรีวิวตรง ๆ** (D-117 ข้อ 4, 5) — เซิร์ฟเวอร์
 * resolve รหัสสินค้าทุกตัวไว้แล้วตอนตรวจไฟล์ หน้าจอจึงไม่ต้องยิงค้นหาเองทีละรหัส
 * (ไฟล์ 200 บรรทัดต้องไม่กลายเป็น 200 คำขอ) และ `uomCode` **ไม่ผ่าน
 * `withResolvedProduct`** โดยตั้งใจ: หน่วยของแถวจาก upload ต้องคงตามไฟล์ที่ลูกค้า
 * ระบุมาเสมอ ไม่ใช่ `base_uom` จาก db — การเขียนทับเงียบ ๆ คือการแก้คำสั่งซื้อของ
 * ลูกค้า
 */
export function salesOrderLineDraftFromPreview(
  line: SalesOrderSpreadsheetPreview['lines'][number],
): SalesOrderLineDraft {
  return {
    customerPoLineNo: line.customer_po_line_no,
    productCode: line.product_code,
    productName: line.product_name,
    model: line.model ?? '',
    quantity: line.quantity,
    uomCode: line.uom_code,
    requestedDeliveryDate: requestedDeliveryDateLabel(line.requested_delivery_date),
    ref1: line.ref1 ?? '',
    ref2: line.ref2 ?? '',
    ref3: line.ref3 ?? '',
    ref4: line.ref4 ?? '',
    ref5: line.ref5 ?? '',
    ref6: line.ref6 ?? '',
  };
}

/**
 * แปลงหัวใบที่พรีวิวอ่านออกมาให้เป็นค่าที่ฟอร์มถือ — **ไม่มี `soNumber`** (D-113)
 * เพราะชนิดข้อมูล `CreateSalesOrderFields` ไม่มีช่องนั้นให้ใส่แล้ว
 */
export function salesOrderFieldsFromPreview(
  preview: SalesOrderSpreadsheetPreview,
): CreateSalesOrderFields {
  return {
    plantCode: preview.plant_code,
    customerCode: preview.customer_code,
    customerPoNumber: preview.customer_po_number,
    note: preview.note ?? '',
  };
}
